import { useEffect } from 'react';

import { helpUrl } from '../lib/helpUrl';

/**
 * In-SPA redirect from `/help/*` on a tenant subdomain to the branding-site
 * help center (#2420).
 *
 * Without this component, an admin typing `/help` (or `/help/<slug>`) into
 * the URL bar while on a tenant page would fall through to the SPA's
 * wildcard route and render the in-app 404 — the user expected the help
 * center, they got a 404. The in-app `?` shortcut and Help icon already
 * cross-domain via `helpUrl()`, but the URL-bar entry path was unguarded.
 *
 * Mounted at `<Route path="/help/*">` in BOTH the authenticated and public
 * branches of `App.tsx` so a logged-out admin typing `/help` still reaches
 * the help center (no auth required to read public help articles).
 *
 * The destination URL preserves the full path tail (`/help/<slug>`), the
 * query string, and the URL fragment so deep-links like
 * `/help/sso-and-saml#service-provider-details` land on the exact branding
 * URL — not just the root TOC.
 *
 * Strategy: `helpUrl('')` already resolves the branding-site base for the
 * current runtime (prod hostname, per-TM dev hostname, or `VITE_HELP_BASE`
 * override). We take that resolved root, strip the trailing `/help`
 * suffix, then concatenate the literal `window.location` path tail. This
 * avoids having to constrain the SPA route to known `HelpSlug` values —
 * an unknown slug still reaches the branding site, where the marketing
 * 404 surfaces the right message.
 */
export default function HelpRedirect() {
    useEffect(() => {
        // Resolve the branding-site help-center base for the current runtime.
        // `helpUrl('')` returns `<base>/help`; strip the `/help` suffix to
        // get the bare base.
        const helpRoot = helpUrl('');
        const base = helpRoot.endsWith('/help') ? helpRoot.slice(0, -'/help'.length) : helpRoot;

        const { pathname, search, hash } = window.location;
        const destination = `${base}${pathname}${search}${hash}`;

        window.location.replace(destination);
    }, []);

    return (
        <div className="loading-container">
            <p>Opening help…</p>
        </div>
    );
}
